import { z } from 'zod';
import { ATTENDANCE_DISPLAY_KIND_VALUES, ATTENDANCE_DISPLAY_STATUS_VALUES } from '@fas/types';
import { roomTagTapSchema } from './cards';
import { nonEmptyTrimmed, paginationQuerySchema, uuidSchema } from './common';
import { devicePublicJwkSchema } from './devices';

// ── Attendance displays and room codes (§37 dynamic QR, Phase 2E, ADR-047) ─────
/** 64-byte ECDSA signature (r‖s), base64url without padding. */
const signatureSchema = z.string().regex(/^[A-Za-z0-9_-]{86}$/, 'Must be a 64-byte base64url signature');

/** Crockford base32 without the ambiguous letters I, L, O and U. */
export const PAIRING_CODE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
export const PAIRING_CODE_LENGTH = 10;

/**
 * A display pairing code as typed: case, spaces and dashes do not matter, and the letters
 * people confuse with digits (O, I, L) are read as 0, 1, 1.
 */
export const pairingCodeSchema = z
  .string('Enter the pairing code')
  .transform((v) => v.toUpperCase().replace(/[\s-]/g, '').replace(/O/g, '0').replace(/[IL]/g, '1'))
  .pipe(z.string().regex(new RegExp(`^[${PAIRING_CODE_ALPHABET}]{${PAIRING_CODE_LENGTH}}$`), `Enter the ${PAIRING_CODE_LENGTH}-character pairing code`));

/** `<D|E><id, 22 base64url>.<window start, base36 seconds>.<HMAC, 22 base64url>` (ADR-047). */
export const QR_CODE_PATTERN = /^[DE][A-Za-z0-9_-]{22}\.[0-9a-z]{1,10}\.[A-Za-z0-9_-]{22}$/;
/**
 * `v1.<D|E|T><id>.<scan time, base36 seconds>.<HMAC, 43 base64url>`, bound to the signed-in user:
 * a display's code, an event's code or an NFC room tag (T, Phase 3G).
 */
export const SCAN_RECEIPT_PATTERN = /^v1\.[DET][A-Za-z0-9_-]{22}\.[0-9a-z]{1,10}\.[A-Za-z0-9_-]{43}$/;

export const qrCodeSchema = z.string('Scan the code again').trim().regex(QR_CODE_PATTERN, 'This is not an attendance code. Scan the code on the display again.');
export const scanReceiptSchema = z.string().regex(SCAN_RECEIPT_PATTERN, 'Scan the code on the display again');

const displayBase = {
  name: nonEmptyTrimmed(100, 'Name'),
  kind: z.enum(ATTENDANCE_DISPLAY_KIND_VALUES),
  locationId: uuidSchema,
  /** A keyboard-wedge card reader is attached (Phase 3G). */
  cardReader: z.boolean(),
};

export const createDisplaySchema = z.object({ ...displayBase, cardReader: displayBase.cardReader.default(false) }).strict();
export type CreateDisplayInput = z.infer<typeof createDisplaySchema>;

export const updateDisplaySchema = z.object(displayBase).partial().strict();
export type UpdateDisplayInput = z.infer<typeof updateDisplaySchema>;

export const revokeDisplaySchema = z.object({ reason: nonEmptyTrimmed(500, 'Reason') }).strict();
export type RevokeDisplayInput = z.infer<typeof revokeDisplaySchema>;

export const listDisplaysQuerySchema = paginationQuerySchema.extend({
  status: z.enum(ATTENDANCE_DISPLAY_STATUS_VALUES).optional(),
  kind: z.enum(ATTENDANCE_DISPLAY_KIND_VALUES).optional(),
  locationId: uuidSchema.optional(),
});
export type ListDisplaysQuery = z.infer<typeof listDisplaysQuerySchema>;

/** The display's key signs `fas-display/v1\nPAIR\n<code>` to prove it holds the private key. */
export const pairDisplaySchema = z
  .object({
    code: pairingCodeSchema,
    publicKey: devicePublicJwkSchema,
    signature: signatureSchema,
  })
  .strict();
export type PairDisplayInput = z.infer<typeof pairDisplaySchema>;

export const displayChallengeSchema = z.object({ displayId: uuidSchema }).strict();
export type DisplayChallengeInput = z.infer<typeof displayChallengeSchema>;

/** Signed `fas-display/v1\nCODE\n<displayId>\n<challenge>`. */
export const displayCodeRequestSchema = z
  .object({
    displayId: uuidSchema,
    challenge: z.string().min(1).max(300),
    signature: signatureSchema,
    /** Include today's timetable of the location (the display asks every few minutes). */
    schedule: z.boolean().optional(),
  })
  .strict();
export type DisplayCodeRequestInput = z.infer<typeof displayCodeRequestSchema>;

/** A scanned display or event code, or a tap of an NFC room tag (Phase 3G). */
export const scanCodeSchema = z.union([z.object({ code: qrCodeSchema }).strict(), z.object({ tag: roomTagTapSchema }).strict()]);
export type ScanCodeInput = z.infer<typeof scanCodeSchema>;
